// Periods stay owned by advertising. This module only builds a read-only view.
export const kyivDay = value => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date(value));

export const periodDates = row => ({
  from: kyivDay(row.starts_at),
  to: kyivDay(new Date(row.ends_at).getTime() - 1),
});

export const clinicLink = id => ({ name: 'Clinic', params: { clinicId: id }, query: { tab: 'topClinics' } });
export const translatedName = name => name && (name.en || name.ru || Object.values(name).find(Boolean));

export function placementView(row) {
  if (!row || typeof row.placement_id !== 'string' || !row.placement_id.trim() || row.placement_id === 'undefined' ||
      !Number.isInteger(row.clinic_id) || row.clinic_id < 1 ||
      !['draft', 'confirmed', 'paused', 'cancelled'].includes(row.status) ||
      !Number.isFinite(Date.parse(row.starts_at)) || !Number.isFinite(Date.parse(row.ends_at)) ||
      Date.parse(row.starts_at) >= Date.parse(row.ends_at) ||
      !Array.isArray(row.illness_kind_ids) || row.illness_kind_ids.some(id => !Number.isInteger(id) || id < 1) ||
      !Number.isInteger(row.sort_order) || !Number.isInteger(row.weight) || row.weight < 0 || row.weight > 100 ||
      typeof row.show_on_homepage !== 'boolean') {
    throw new Error('TOP placement data is incomplete. Please retry.');
  }
  return { ...row, illness_kind_ids: [...new Set(row.illness_kind_ids)].sort((a, b) => a - b) };
}

export function overviewView(data, reservations = false) {
  if (!data || data.error || !Array.isArray(data.clinic_top_placements) ||
      !Number.isInteger(data.slot_limit) || data.slot_limit < 1 ||
      typeof data.has_more !== 'boolean' || (reservations && data.has_more)) {
    throw new Error('Could not load TOP placements. Please retry.');
  }
  const seen = new Set();
  const rows = data.clinic_top_placements.map(row => {
    const parsed = placementView(row);
    if (seen.has(parsed.placement_id) || (reservations && parsed.status !== 'confirmed')) {
      throw new Error('TOP placement data is incomplete. Please retry.');
    }
    seen.add(parsed.placement_id);
    return parsed;
  });
  return {
    rows,
    slotLimit: data.slot_limit,
    hasMore: data.has_more,
    clinics: (data.clinics || []).reduce((map, clinic) => ({ ...map, [clinic.id]: clinic }), {}),
    labelsUnavailable: data.clinic_labels_unavailable === true,
  };
}

export const requestState = () => ({ generation: 0, loading: false, error: '', view: null });

export function invalidate(state) {
  state.generation++;
  state.view = null;
  state.error = '';
  state.loading = false;
}

// A failed/new request invalidates the old view; a late response cannot restore it.
export async function loadLatest(state, request, parse) {
  const generation = ++state.generation;
  state.loading = true;
  state.error = '';
  state.view = null;
  try {
    const response = await request();
    const view = parse(response.data);
    if (generation === state.generation) state.view = view;
  } catch (error) {
    if (generation === state.generation) state.error = error.message || 'Could not load data. Please retry.';
  } finally {
    if (generation === state.generation) state.loading = false;
  }
}

export function slotsFor(view, directionId) {
  const clinics = new Set();
  return view.rows
    .filter(row => directionId === 'homepage' ? row.show_on_homepage :
      row.illness_kind_ids.length === 0 || row.illness_kind_ids.includes(directionId))
    .slice().sort((a, b) => b.weight - a.weight || a.sort_order - b.sort_order ||
      a.clinic_id - b.clinic_id || a.placement_id.localeCompare(b.placement_id))
    .filter(row => {
      if (clinics.has(row.clinic_id)) return false;
      clinics.add(row.clinic_id);
      return true;
    });
}

// Analytics is a view of the complete reservations response, never registry pagination.
export function reservationStats(view, catalog, on) {
  if (!view || !catalog || !on) return null;
  const ids = new Set(catalog.map(direction => direction.id));
  view.rows.forEach(row => row.illness_kind_ids.forEach(id => ids.add(id)));
  const counts = [...ids].map(id => slotsFor(view, id).length);
  const day = Date.parse(on + 'T12:00:00Z');
  const lastDay = new Date(day + 6 * 86400000).toISOString().slice(0, 10);
  return {
    clinics: new Set(view.rows.map(row => row.clinic_id)).size,
    homepage: slotsFor(view, 'homepage').length,
    free: counts.reduce((sum, count) => sum + Math.max(0, view.slotLimit - count), 0),
    full: counts.filter(count => count >= view.slotLimit).length,
    directions: counts.length,
    ending: view.rows.filter(row => {
      const end = periodDates(row).to;
      return end >= on && end <= lastDay;
    }).sort((a, b) => a.ends_at.localeCompare(b.ends_at)),
  };
}
